import { useCallback } from 'react'
import { showToast } from '@quickmd-llc/design-system'
import { errorCodes, getUserFriendlyErrorMessage } from '@/utils/errormessages'

/**
 * Returns a stable callback that, given a formatted error string (output of
 * `getUserFriendlyErrorMessage`), calls DS `showToast` with `type:'error'` and
 * `position:'bottom-right'`. No-op for falsy input.
 *
 * Mapping mirrors `new-members.tsx`'s error-toast useEffect and
 * `IntakeForms/util.jsx`'s `toastErrorMessGenerator`:
 *
 * | Input (formatted string)                    | Toast message                         |
 * |---------------------------------------------|---------------------------------------|
 * | NO_CONNECTION formatted                      | same string                           |
 * | FILL_ALL_FIELDS formatted                    | same string                           |
 * | DEFAULT formatted                            | same string                           |
 * | NEED_AT_LEAST_ONE_QUESTION formatted         | same string  (intake)                 |
 * | NEED_SAVE_AT_LEAST_ONE_QUESTION formatted    | same string  (intake)                 |
 * | NAME_ALREADY_EXISTS+Email fmt                | FILL_ALL_FIELDS message               |
 * | PROVIDER_NUMBER_NOT_UNIQUE fmt               | FILL_ALL_FIELDS message               |
 * | DUPLICATE_LICENSES formatted                 | same string                           |
 * | MAX_CHARACTER_LIMIT formatted                | FILL_ALL_FIELDS message  (intake)     |
 * | NAME_ALREADY_EXISTS+'Intake form' fmt        | FILL_ALL_FIELDS message  (intake)     |
 * | FIELD_REQUIRED+'Question type' fmt           | FILL_ALL_FIELDS message  (intake)     |
 * | FIELD_REQUIRED+'File format(s)' fmt          | FILL_ALL_FIELDS message  (intake)     |
 * | FIELD_REQUIRED+'Intake name' fmt             | FILL_ALL_FIELDS message  (intake)     |
 * | FIELD_REQUIRED+'Question' fmt                | FILL_ALL_FIELDS message  (intake)     |
 * | FIELD_REQUIRED+'Option' fmt                  | FILL_ALL_FIELDS message  (intake)     |
 * | unknown / any other truthy                   | DEFAULT message                       |
 */
export const useErrorToast = (): ((errorCode: string | null | undefined) => void) => {
  return useCallback((errorCode: string | null | undefined) => {
    if (!errorCode) return

    const fillAllFields = getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)
    const noConnection = getUserFriendlyErrorMessage(errorCodes.NO_CONNECTION)
    const defaultError = getUserFriendlyErrorMessage(errorCodes.DEFAULT)
    const nameAlreadyExists = getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Email')
    const providerNumberNotUnique = getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE)
    const duplicateLicenses = getUserFriendlyErrorMessage(errorCodes.DUPLICATE_LICENSES, null)

    // Intake: own-text codes (NEED_* → toast their own message, same as original util.jsx)
    const needAtLeastOneQuestion = getUserFriendlyErrorMessage(errorCodes.NEED_AT_LEAST_ONE_QUESTION)
    const needSaveAtLeastOneQuestion = getUserFriendlyErrorMessage(errorCodes.NEED_SAVE_AT_LEAST_ONE_QUESTION)

    // Intake: FILL_ALL_FIELDS-collapse family (original util.jsx → all map to FILL_ALL_FIELDS text)
    const maxCharacterLimit = getUserFriendlyErrorMessage(errorCodes.MAX_CHARACTER_LIMIT)
    const nameAlreadyExistsIntakeForm = getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Intake form')
    const fieldRequiredQuestionType = getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Question type')
    const fieldRequiredFileFormats = getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'File format(s)')
    const fieldRequiredIntakeName = getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Intake name')
    const fieldRequiredQuestion = getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Question')
    const fieldRequiredOption = getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Option')

    let message: string

    if (errorCode === noConnection || errorCode === fillAllFields || errorCode === defaultError) {
      message = errorCode
    } else if (errorCode === needAtLeastOneQuestion || errorCode === needSaveAtLeastOneQuestion) {
      // Intake: NEED_* codes → own message text (mirrors original util.jsx case-fall-through)
      message = errorCode
    } else if (errorCode === nameAlreadyExists || errorCode === providerNumberNotUnique) {
      message = fillAllFields
    } else if (errorCode === duplicateLicenses) {
      message = duplicateLicenses
    } else if (
      errorCode === maxCharacterLimit ||
      errorCode === nameAlreadyExistsIntakeForm ||
      errorCode === fieldRequiredQuestionType ||
      errorCode === fieldRequiredFileFormats ||
      errorCode === fieldRequiredIntakeName ||
      errorCode === fieldRequiredQuestion ||
      errorCode === fieldRequiredOption
    ) {
      // Intake: FILL_ALL_FIELDS-collapse family → toast FILL_ALL_FIELDS text
      message = fillAllFields
    } else {
      message = defaultError
    }

    showToast({ type: 'error', message, position: 'bottom-right' })
  }, [])
}
